import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Popover,
  Stack,
  Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SyncIcon from '@mui/icons-material/Sync';
import HistoryIcon from '@mui/icons-material/History';
import api from '../services/api';

export const SYNC_PROGRESS_STEPS = [
  { key: 'entraSync', label: 'Sync Entra Users' },
  { key: 'mailboxTypeSync', label: 'Sync Mailbox Types' },
  { key: 'securitySync', label: 'Sync Security Context' },
  { key: 'usageIntelSync', label: 'Sync Usage Intelligence' },
  { key: 'classification', label: 'Re-classify Accounts' }
];

const STEP_LABELS = Object.fromEntries(SYNC_PROGRESS_STEPS.map((s) => [s.key, s.label]));

function formatElapsed(seconds) {
  if (seconds == null) return '';
  const m = Math.floor(seconds / 60);
  const s = String(seconds % 60).padStart(2, '0');
  return `${m}:${s}`;
}

function formatEta(processed, total, elapsedSeconds) {
  if (!(total > 0 && processed > 0 && processed < total && elapsedSeconds > 0)) return null;
  const rate = processed / Math.max(elapsedSeconds, 1);
  const eta = Math.ceil((total - processed) / rate);
  return `~${Math.floor(eta / 60)}:${String(eta % 60).padStart(2, '0')} remaining`;
}

export function buildProgressSteps(progress, jobType) {
  if (!progress?.steps) {
    return SYNC_PROGRESS_STEPS.map((s) => ({
      ...s,
      status: 'pending',
      processed: 0,
      total: 0,
      message: null
    }));
  }
  if (jobType && jobType !== 'Full' && !Array.isArray(progress.steps)) {
    // keep ordered known steps when available
  }
  if (Array.isArray(progress.steps)) return progress.steps;
  return SYNC_PROGRESS_STEPS.map((s) => ({
    ...s,
    ...(progress.steps[s.key] || { status: 'pending', processed: 0, total: 0, message: null })
  }));
}

export function getOverallSyncPercent(progressSteps) {
  if (!progressSteps?.length) return null;
  let weighted = 0;
  let weight = 0;
  for (const step of progressSteps) {
    const t = step.total || 0;
    const p = step.processed || 0;
    if (t > 0) {
      weighted += Math.min(1, p / t);
      weight += 1;
    } else if (step.status === 'completed') {
      weighted += 1;
      weight += 1;
    } else if (step.status === 'running') {
      weighted += 0.15;
      weight += 1;
    } else {
      weight += 1;
    }
  }
  if (!weight) return null;
  return Math.min(99, Math.round((weighted / weight) * 100));
}

export function getCurrentStepLabel(progress, progressSteps) {
  const key = progress?.currentStep;
  if (key) {
    const fromSteps = progressSteps?.find((s) => s.key === key);
    return fromSteps?.label || STEP_LABELS[key] || key;
  }
  const running = progressSteps?.find((s) => s.status === 'running');
  return running?.label || null;
}

/** Query keys shared by navbar + dashboard sync UI */
export const SYNC_QUERY_KEYS = {
  navbarLatest: ['navbar-latest-sync'],
  navbarRunning: ['navbar-running-sync'],
  dashboardLatest: ['dashboard', 'latest-sync']
};

/** Immediately clear "running" UI after cancel / completion */
export function markSyncIdleInCache(queryClient, { status = 'Cancelled', jobPatch = {} } = {}) {
  queryClient.setQueryData(SYNC_QUERY_KEYS.navbarRunning, {
    success: true,
    job: null,
    isRunning: false,
    progress: null
  });

  queryClient.setQueryData(SYNC_QUERY_KEYS.navbarLatest, (old) => {
    if (!old?.job) return old;
    return {
      ...old,
      job: {
        ...old.job,
        status,
        success: false,
        completedAt: new Date().toISOString(),
        ...jobPatch
      },
      progress: null
    };
  });

  queryClient.setQueryData(SYNC_QUERY_KEYS.dashboardLatest, (old) => {
    if (!old?.job) return old;
    return {
      ...old,
      job: {
        ...old.job,
        status,
        success: false,
        completedAt: new Date().toISOString(),
        ...jobPatch
      },
      progress: null
    };
  });
}

export async function refreshAllSyncQueries(queryClient) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: SYNC_QUERY_KEYS.navbarLatest }),
    queryClient.invalidateQueries({ queryKey: SYNC_QUERY_KEYS.navbarRunning }),
    queryClient.invalidateQueries({ queryKey: SYNC_QUERY_KEYS.dashboardLatest })
  ]);
}

/**
 * Compact sync health chip + popover with live stage progress.
 * Used in the navbar; click for details / cancel / history.
 */
export default function SyncStatusPopover({ onNavigateHistory, chipSx = {} }) {
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const open = Boolean(anchorEl);

  const latestQuery = useQuery({
    queryKey: SYNC_QUERY_KEYS.navbarLatest,
    queryFn: async () => {
      const response = await api.get('/api/sync/jobs/latest');
      return response.data || null;
    },
    staleTime: 5_000,
    refetchInterval: (query) => {
      const status = query.state.data?.job?.status;
      return status === 'Running' ? 3000 : 30_000;
    }
  });

  const latestIsRunning = latestQuery.data?.job?.status === 'Running';

  const runningQuery = useQuery({
    queryKey: SYNC_QUERY_KEYS.navbarRunning,
    queryFn: async () => {
      const response = await api.get('/api/sync/jobs/running');
      return response.data || null;
    },
    enabled: open || latestIsRunning,
    staleTime: 0,
    refetchInterval: (query) => {
      const running = query.state.data?.isRunning || query.state.data?.job?.status === 'Running';
      return running || latestIsRunning ? 3000 : false;
    }
  });

  // Prefer the authoritative latest job when it is no longer Running —
  // otherwise a stale running-query cache keeps the chip on "Syncing".
  const latestJob = latestQuery.data?.job || null;
  const runningJob =
    runningQuery.data?.isRunning && runningQuery.data?.job?.status === 'Running'
      ? runningQuery.data.job
      : null;
  const job = runningJob || latestJob;
  const progress =
    (runningJob && runningQuery.data?.progress) ||
    (latestJob?.status === 'Running' ? latestQuery.data?.progress || latestJob?.metadata : null) ||
    null;

  const isRunning = Boolean(
    latestJob?.status === 'Running' ||
      (runningQuery.data?.isRunning && runningQuery.data?.job?.status === 'Running')
  );
  const isFailed = job?.status === 'Failed';
  const isCancelled = job?.status === 'Cancelled';
  const startedAt = job?.startedAt ? new Date(job.startedAt) : null;
  const elapsedSeconds =
    isRunning && startedAt
      ? Math.max(0, Math.floor((Date.now() - startedAt.getTime()) / 1000))
      : null;

  const progressSteps = useMemo(
    () => buildProgressSteps(progress, job?.jobType),
    [progress, job?.jobType]
  );
  const overallPct = useMemo(() => getOverallSyncPercent(progressSteps), [progressSteps]);
  const currentStepLabel = useMemo(
    () => getCurrentStepLabel(progress, progressSteps),
    [progress, progressSteps]
  );

  const startedTooLong =
    isRunning &&
    startedAt &&
    Date.now() - startedAt.getTime() > 45 * 60 * 1000;

  const chip = useMemo(() => {
    if (isRunning && !startedTooLong) {
      const pctPart = overallPct != null ? ` ${overallPct}%` : '';
      return {
        label: `Syncing${pctPart}`,
        subtitle: currentStepLabel,
        dot: '#fbbf24',
        bgcolor: 'rgba(251, 191, 36, 0.15)',
        border: 'rgba(251, 191, 36, 0.35)',
        color: '#fef3c7'
      };
    }
    if (isFailed || startedTooLong) {
      return {
        label: startedTooLong ? 'Sync Stale' : 'Sync Failed',
        subtitle: null,
        dot: '#f87171',
        bgcolor: 'rgba(248, 113, 113, 0.15)',
        border: 'rgba(248, 113, 113, 0.35)',
        color: '#fee2e2'
      };
    }
    if (isCancelled) {
      return {
        label: 'Sync Cancelled',
        subtitle: null,
        dot: '#fb923c',
        bgcolor: 'rgba(251, 146, 60, 0.15)',
        border: 'rgba(251, 146, 60, 0.35)',
        color: '#ffedd5'
      };
    }
    return {
      label: 'Operational',
      subtitle: null,
      dot: '#4ade80',
      bgcolor: 'rgba(74, 222, 128, 0.15)',
      border: 'rgba(74, 222, 128, 0.3)',
      color: '#e0f2e9'
    };
  }, [isRunning, isFailed, isCancelled, startedTooLong, overallPct, currentStepLabel]);

  const currentStep = progress?.currentStep
    ? progressSteps.find((s) => s.key === progress.currentStep)
    : progressSteps.find((s) => s.status === 'running');
  const eta =
    currentStep &&
    formatEta(currentStep.processed || 0, currentStep.total || 0, elapsedSeconds || 0);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await api.post('/api/sync/cancel');
      markSyncIdleInCache(queryClient, { status: 'Cancelled' });
      await refreshAllSyncQueries(queryClient);
    } catch {
      await refreshAllSyncQueries(queryClient);
    } finally {
      setCancelling(false);
    }
  };

  const lastSuccess = latestQuery.data?.lastSuccessfulJob;
  const completedAt = lastSuccess?.completedAt
    ? new Date(lastSuccess.completedAt)
    : job?.status === 'Completed' && job?.completedAt
      ? new Date(job.completedAt)
      : null;

  return (
    <>
      <Chip
        icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: chip.dot }} />}
        label={
          isRunning && overallPct != null
            ? `${chip.label}`
            : chip.label
        }
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          bgcolor: chip.bgcolor,
          border: `1px solid ${chip.border}`,
          color: chip.color,
          fontWeight: 600,
          fontSize: '0.75rem',
          cursor: 'pointer',
          '& .MuiChip-icon': { marginLeft: '8px' },
          '&:hover': { filter: 'brightness(1.08)' },
          ...chipSx
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1.25,
              width: 360,
              maxWidth: 'calc(100vw - 24px)',
              borderRadius: 2.5,
              boxShadow: '0 12px 40px rgba(15, 23, 42, 0.18)',
              overflow: 'hidden'
            }
          }
        }}
      >
        <Box sx={{ px: 2, py: 1.75, bgcolor: isRunning ? alpha('#2563eb', 0.06) : 'grey.50' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <SyncIcon
                sx={{
                  fontSize: 18,
                  color: isRunning ? 'primary.main' : isFailed ? 'error.main' : 'success.main',
                  ...(isRunning ? { animation: 'spin 1.2s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } } : {})
                }}
              />
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  {isRunning
                    ? `Sync in progress${elapsedSeconds != null ? ` · ${formatElapsed(elapsedSeconds)}` : ''}`
                    : isFailed
                      ? 'Last sync failed'
                      : isCancelled
                        ? 'Sync cancelled'
                        : 'Sync status'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {isRunning
                    ? [currentStepLabel, overallPct != null ? `${overallPct}% overall` : null, eta]
                        .filter(Boolean)
                        .join(' · ') || 'Working…'
                    : isCancelled
                      ? 'Cancelled by user — start a new sync from the Dashboard when ready'
                      : completedAt
                        ? `Last success ${completedAt.toLocaleDateString()} ${completedAt.toLocaleTimeString()}`
                        : 'No recent sync'}
                </Typography>
              </Box>
            </Stack>
            {overallPct != null && isRunning && (
              <Chip label={`${overallPct}%`} size="small" color="primary" sx={{ fontWeight: 700 }} />
            )}
          </Stack>
          {isRunning && (
            <LinearProgress
              variant={overallPct != null ? 'determinate' : 'indeterminate'}
              value={overallPct ?? 0}
              sx={{ mt: 1.5, height: 6, borderRadius: 3 }}
            />
          )}
        </Box>

        <Box sx={{ px: 2, py: 1.5 }}>
          {isRunning || progress?.steps ? (
            <Stack spacing={1.25}>
              {progressSteps.map((step) => {
                const ss = step.status || 'pending';
                const t = step.total || 0;
                const p = step.processed || 0;
                const pct = t > 0 ? Math.min(100, Math.round((p / t) * 100)) : ss === 'completed' ? 100 : 0;
                const variant =
                  t > 0 || ss === 'completed'
                    ? 'determinate'
                    : ss === 'running'
                      ? 'indeterminate'
                      : 'determinate';
                const color = ss === 'failed' ? 'error' : ss === 'completed' ? 'success' : 'primary';
                const cur = isRunning && (progress?.currentStep === step.key || ss === 'running');
                const label =
                  t > 0
                    ? `${pct}% · ${p.toLocaleString()} / ${t.toLocaleString()}`
                    : ss === 'completed'
                      ? 'Done'
                      : ss === 'running'
                        ? 'In progress'
                        : ss === 'failed'
                          ? 'Failed'
                          : 'Pending';
                return (
                  <Box
                    key={step.key}
                    sx={(theme) => ({
                      border: '1px solid',
                      borderColor:
                        ss === 'completed'
                          ? alpha(theme.palette.success.main, 0.3)
                          : ss === 'failed'
                            ? alpha(theme.palette.error.main, 0.3)
                            : cur
                              ? alpha(theme.palette.primary.main, 0.4)
                              : 'divider',
                      borderRadius: 1.5,
                      p: 1.25,
                      bgcolor:
                        ss === 'completed'
                          ? alpha(theme.palette.success.main, 0.05)
                          : cur
                            ? alpha(theme.palette.primary.main, 0.04)
                            : 'transparent'
                    })}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                      <Typography variant="caption" fontWeight={600}>
                        {step.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {label}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant={variant}
                      value={variant === 'determinate' ? pct : 0}
                      color={color}
                      sx={{ height: 5, borderRadius: 2 }}
                    />
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              {lastSuccess?.accountsProcessed != null
                ? `Last run processed ${(lastSuccess.accountsProcessed ?? 0).toLocaleString()} accounts${
                    lastSuccess.duration != null ? ` in ${lastSuccess.duration}s` : ''
                  }.`
                : 'Tenant sync is idle. Start a sync from the Dashboard.'}
            </Typography>
          )}
        </Box>

        <Divider />
        <Stack direction="row" spacing={1} sx={{ px: 2, py: 1.25 }} justifyContent="space-between">
          <Button
            size="small"
            startIcon={<HistoryIcon sx={{ fontSize: 16 }} />}
            onClick={() => {
              setAnchorEl(null);
              onNavigateHistory?.();
            }}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Sync History
          </Button>
          {isRunning && (
            <Button
              size="small"
              color="error"
              variant="outlined"
              disabled={cancelling}
              startIcon={cancelling ? <CircularProgress size={12} /> : null}
              onClick={handleCancel}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Cancel
            </Button>
          )}
        </Stack>
      </Popover>
    </>
  );
}
